import React, { useEffect, useRef, useState } from 'react';
import GridConfig from './GridConfig';
import { compose } from 'recompose';
import { Button, withStyles } from '@material-ui/core';
import WithModal from '../../../../../Dispatch/RouteExecution/Pickups/PickupManagement/components/WithModal';
import DataGrid from '@dataGrid';
import { useReactToPrint } from 'react-to-print';
import { generateDPReportPrintLog, getDataToPrint } from '../utils';
import PrintComponent from '../PrintComponent';
import '../print.scss';

const styles = (theme) => ({
  dataGrid: {
    border: `1px solid ${theme.AppBGColor}`,
    height: '90%',
    '& .ag-theme-material .ag-body-horizontal-scroll': {
      borderWidth: '0px'
    },
    '& .ag-theme-material .ag-icon-checkbox-checked': {
      color: theme.HeaderSelectedText
    },
    '& .ag-theme-material .checkBoxHeader': {
      display: 'flex',
      paddingLeft: 24
    },
    [`&>.gridTheme-${theme.themeName}>.ag-theme-material.theme-${theme.themeName}`]: {
      height: '100%',
    },
    '& .statusBar': {
      display: 'none'
    },
    '& .ag-center-cols-viewport': {
      width: `calc(100% + 18px)`
    }
  },
  container: {
    height: '100%',
    margin: '8px 24px 16px 24px'
  },
  actionContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: 16,
    paddingRight : 24
  },
  mainContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  cancelButton: {
    marginRight: 16
  },
  subTitle: {
    fontSize: theme.textFontSize,
    color: theme.dispatchPanelHeaderColor,
    marginBottom: 8
  },
  actionButton: {
    textTransform: 'none',
    '&$actionDisabled': {
      backgroundColor: theme.headerIconColor,
      color: theme.TextColor,
      cursor: 'not-allowed',
      opacity: 0.7
    }
  },
  actionDisabled: {
    backgroundColor: theme.iconColor,
    color: theme.TextColor
  },
  circularProgress: {
    color: theme.white
  }
});

const PrintManifestDialog = ({
  classes,
  handleClose,
  dispatchData: {
    driverRoutesData = {},
    driverData = {},
    clientCodeLists = {},
    enableReturnExceptionReasonLabels = false
  } = {},
}) => {
  const gridConfig = useRef(new GridConfig());
  const gridApi = useRef(null);
  const containerRef = useRef();
  const promiseResolveRef = useRef(null);
  const [printData, setPrintData] = useState([]);
 
  useEffect(() => {
    if (printData && promiseResolveRef.current) {
      // Resolves the Promise, letting `react-to-print` know that the DOM updates are completed
      promiseResolveRef.current();
    }
  }, [printData]);

  const onBeforeGetContent = () => {
    const selectedRouteIds = gridApi?.current?.getSelectedRows().map((row) => row.routeId);
    generateDPReportPrintLog(driverData?.driverNumber, selectedRouteIds);
    const dataToPrint = getDataToPrint(
      driverRoutesData.filter((route) => selectedRouteIds.includes(route.routeId)),
      driverData,
      clientCodeLists,
      enableReturnExceptionReasonLabels
    );
    return new Promise((resolve) => {
      promiseResolveRef.current = resolve;
      setPrintData(dataToPrint);
    });
  };

  const handlePrint = useReactToPrint({
    content: () => containerRef.current,
    onBeforeGetContent: onBeforeGetContent,
    copyStyles: true,
    documentTitle : "Driver Performance Report"
  });

  const onGridReady = ({ api }) => {
    gridApi.current = api;
    gridApi.current.selectAll();
  };

  const handlePrintClick = () => {
    handlePrint();
  };

  return (
    <div className={classes.mainContainer}>
      <div className={classes.container}>
        <div className={classes.subTitle}>Select manifests to print</div>
        <div className={classes.dataGrid}>
          <DataGrid
            defaultColDef={gridConfig.current.config.defaultColDef}
            columnDefs={gridConfig.current.config.columns}
            config={gridConfig.current.config}
            rowData={driverRoutesData}
            onGridReady={onGridReady}
            deltaRowDataMode={true}
            getRowId={(params) => params.data.routeId}
            rowSelection={gridConfig.current.config.rowSelection}
            rowDeselection={gridConfig.current.config.rowDeselection}
          />
        </div>
      </div>
      <div id="actionItems" className={classes.actionContainer}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleClose}
          className={classes.cancelButton}>
          Cancel
        </Button>
        <Button
          classes={{ root: classes.actionButton, disabled: classes.actionDisabled }}
          variant="contained"
          color="primary"
          onClick={handlePrintClick}>
          Print
        </Button>
      </div>
      <PrintComponent containerRef={containerRef} printData={printData} driverData={driverData}/>
    </div>
  );
};

export default compose(WithModal, withStyles(styles))(PrintManifestDialog);
